import { NODE_LABELS, type NodeLabel, type RelationshipType } from '@atbash-labs/ontology-core'

/**
 * Replacement for the old `domain-layers.ts` after the SCADA ontology
 * restructure (PR1). The "layer" concept has been removed from the ontology;
 * the graph is a single connected mesh. This module retains the lightweight
 * visual-grouping primitives the GraphPage NVL renderer still needs:
 *
 * - Anchor / sub-anchor labels for sizing important nodes
 * - A single neutral palette for nodes and edges
 * - A small set of relationship types that visually deserve a "spans groupings"
 *   cue (cross-domain mappings such as `MAPS_TO_TAG` or `MAPS_TO_TYPE`).
 *
 * No `DomainLayer` enum and no per-label domain mapping — everything is a
 * peer in the new ontology.
 */

/** Largest nodes — the top of the graph hierarchy (tenant + locations). */
export const ANCHOR_NODE_LABELS = new Set<NodeLabel>(['Organization', 'Site'])

/** Mid-size grouping nodes between anchors and regular nodes. */
export const SUBANCHOR_NODE_LABELS = new Set<NodeLabel>([
  'TagProvider',
  'Compute',
  'Database',
  'Controller'
])

/**
 * Labels that act as the "root" of a structural cluster in the graph layout.
 *
 * The renderer groups every node with its *nearest* anchor (by graph distance),
 * so anchors nest naturally: a `UDTDefinition` sitting under a `TagProvider`
 * starts its own cluster, and its members — one hop away — join the UDT cluster
 * instead of the further provider. Each anchor instance becomes its own cluster
 * (every Controller, every TagProvider, every UDT definition/instance, …).
 *
 * Add a label here to make that container type spawn its own sub-cluster.
 */
export const CLUSTER_ANCHOR_LABELS = new Set<NodeLabel>([
  'Compute',
  'TagProvider',
  'Database',
  'Controller',
  'PLCDataType',
  'UDTDefinition',
  'UDTInstance'
])

/**
 * Relationships that conceptually bridge "domains" of the ontology (Tag ↔
 * PLCTag, Process ↔ Equipment via observation, etc.). The renderer uses these
 * to nudge edge color/width so users can spot cross-source mappings at a
 * glance. This is purely visual.
 */
export const CROSS_DOMAIN_RELATIONSHIP_TYPES = new Set<RelationshipType>([
  'MAPS_TO_TAG',
  'MAPS_TO_TYPE',
  'MERGED_WITH',
  'MATERIALIZES'
])

/** Single neutral fill used as a generic default; renderer picks size from anchor sets. */
export const DEFAULT_NODE_COLOR = '#94a3b8'

/** Fallback fill for any node whose label is not part of the published ontology. */
export const UNRECOGNIZED_NODE_COLOR = '#9ca3af'

/**
 * Converts an HSL triple (h in degrees, s/l in 0–1) to a `#rrggbb` hex string.
 * Used only to synthesize the per-label palette below; kept dependency-free.
 */
function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = (((h % 360) + 360) % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0
  let g = 0
  let b = 0
  if (hp < 1) [r, g] = [c, x]
  else if (hp < 2) [r, g] = [x, c]
  else if (hp < 3) [g, b] = [c, x]
  else if (hp < 4) [g, b] = [x, c]
  else if (hp < 5) [r, b] = [x, c]
  else [r, b] = [c, x]
  const m = l - c / 2
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * A distinct color for every node label the ontology directly supports.
 *
 * Hues are spread around the wheel by the golden angle (~137.5°) so adjacent
 * entries in `NODE_LABELS` land far apart and the whole set stays visually
 * separable regardless of how many labels exist. Saturation/lightness are fixed
 * so colors read consistently on the dark graph canvas. The exact colors are
 * arbitrary and deterministic — they only need to differ per type.
 */
export const NODE_TYPE_COLORS: Record<string, string> = Object.fromEntries(
  NODE_LABELS.map((label, index) => [label, hslToHex(index * 137.508, 0.62, 0.58)])
)

/** Returns the palette color for a label, or the gray fallback if unrecognized. */
export function colorForNodeLabel(label: NodeLabel | string): string {
  return NODE_TYPE_COLORS[label] ?? UNRECOGNIZED_NODE_COLOR
}

/** Edge palette — intra group vs cross-domain (slight grey shift). */
export const EDGE_COLOR_INTRA = '#64748b'
export const EDGE_COLOR_CROSS = '#94a3b8'

export type NodeAnchorTier = 'primary' | 'sub' | 'normal'

export function nodeAnchorTier(label: NodeLabel | string): NodeAnchorTier {
  if (ANCHOR_NODE_LABELS.has(label as NodeLabel)) return 'primary'
  if (SUBANCHOR_NODE_LABELS.has(label as NodeLabel)) return 'sub'
  return 'normal'
}
